import { Link } from "react-router-dom";
import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { celebrate } from "@/lib/confetti";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, TrendingUp, Sparkles, Rocket } from "lucide-react";

/**
 * OrganizerProGate
 * ----------------
 * Wraps organizer-only Pro features (AI Flyer Studio, Event Predictions,
 * AI vendor suggestions, unlimited AI contracts). If the caller is an
 * Organizer Pro subscriber, an active trialer, or the platform admin,
 * children render as usual. Otherwise a compact, on-brand paywall card is
 * shown instead — with a "Start 7-day free trial" CTA when the organizer
 * hasn't burned their trial yet, or an "Upgrade" CTA once they have.
 *
 * Props:
 *   feature   -> short display name (e.g. "AI Flyer Studio")
 *   pitch     -> one-line description shown under the title
 *   perks     -> array of bullet strings (max 5) to preview the value
 *   testIdKey -> unique kebab-case suffix used to build data-testids
 *   children  -> the real component tree, only rendered for Pro/trial/admin
 */
export default function OrganizerProGate({
  feature,
  pitch,
  perks,
  testIdKey,
  children,
}) {
  const { user, refresh } = useAuth();
  const [starting, setStarting] = useState(false);

  // Not logged in yet → let the child decide (usually it will 401 and the
  // outer route guard will redirect). Cheap to short-circuit here.
  if (!user) return children;

  const isAdmin = !!user.is_admin;
  const isPro = (user.plan || "free") === "pro";
  const isTrialing = !!user.on_trial;
  const trialAvailable = !!user.trial_available;

  // Everyone but a free-non-trialing ORGANIZER gets through — vendors won't
  // mount these cards anyway, so this is just a defensive pass-through.
  if (isAdmin || isPro || isTrialing || user.role !== "organizer") return children;

  const startTrial = async () => {
    setStarting(true);
    try {
      await api.post("/organizer/start-trial");
      celebrate({ particleCount: 120, scalar: 1.1 });
      await refresh?.();
      toast.success("Your 7-day Organizer Pro trial is live — every AI feature is unlocked.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start trial");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div
      className="card-warm rounded-xl p-8"
      data-testid={`org-pro-gate-${testIdKey}`}
    >
      <div
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full"
        style={{ backgroundColor: "rgba(249,115,22,0.12)", color: "var(--avt-orange)" }}
      >
        <Lock className="h-3 w-3" /> Organizer Pro
      </div>
      <h3 className="font-serif text-2xl mt-3">{feature}</h3>
      {pitch && (
        <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
          {pitch}
        </p>
      )}
      {perks?.length > 0 && (
        <ul
          className="mt-4 space-y-2 text-sm"
          data-testid={`org-pro-gate-${testIdKey}-perks`}
        >
          {perks.slice(0, 5).map((p) => (
            <li key={p} className="flex items-start gap-2">
              <Sparkles
                className="h-4 w-4 mt-0.5 flex-shrink-0"
                style={{ color: "var(--avt-orange)" }}
              />
              {p}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        {trialAvailable ? (
          <button
            type="button"
            onClick={startTrial}
            disabled={starting}
            data-testid={`org-pro-gate-${testIdKey}-trial`}
            className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Rocket className="h-4 w-4" />
            {starting ? "Starting…" : "Start 7-day free trial"}
          </button>
        ) : (
          <Link
            to="/pricing?role=organizer"
            data-testid={`org-pro-gate-${testIdKey}-upgrade`}
            className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2"
          >
            <TrendingUp className="h-4 w-4" />
            See Organizer Pro
          </Link>
        )}
        <span className="text-xs" style={{ color: "var(--ink-soft)" }}>
          {trialAvailable
            ? "No card required · every AI feature unlocked for 7 days"
            : "$29/mo · reduced 3% platform fee on paid booth fees"}
        </span>
      </div>
    </div>
  );
}
